import { useAuth } from '@hourly/contexts/AuthContext';
import { useEffect, useState } from 'react';
import BankAccount from './BankAccount';
import Completed from './Completed';
import StepContainer, { type StepTrackerItem } from './components/StepContainer';
import { StepKey } from './data/models/step.model';
import { useGetOnboarding } from './data/useGetOnboarding';
import Greetings from './Greetings';
import PartnerDetails from './PartnerDetails';
import SignDocuments from './SignDocuments';

export default function Onboarding() {
  const [step, setStep] = useState<StepKey>('Greetings');
  const {
    sessionState: { partnerId },
  } = useAuth();

  const { onboarding, fetchGetOnboarding } = useGetOnboarding(partnerId!);

  const stepTrackerSteps: StepTrackerItem[] = onboarding.steps.map((step) => {
    const isStepAvailable = onboarding.unlockedSteps.includes(step.key);

    return {
      key: step.key,
      isCompleted: isStepAvailable && step.isStepCompleted,
      isDisabled: !isStepAvailable,
    };
  });

  const handleContinue = async () => {
    const onboardingUpdated = await fetchGetOnboarding();
    const nextStep = onboardingUpdated!.getNextAvailableStep(step);
    setStep(nextStep);
  };

  const handlePreviousStep = () => {
    const previousStep = onboarding.getPreviousAvailableStep(step);
    setStep(previousStep);
  };

  useEffect(() => {
    fetchGetOnboarding(null, {
      onSuccess: (onboardingData) => {
        setStep(onboardingData.initialOnboardingStep);
      },
    });
  }, [fetchGetOnboarding]);

  if (step === 'Greetings') {
    return (
      <StepContainer
        disabled={onboarding.partner.isActive}
        selectedStep={step}
        steps={stepTrackerSteps}
        onClickStep={(stepClicked) => setStep(stepClicked as StepKey)}>
        <Greetings
          salesRepresentative={onboarding.salesRepresentative}
          onContinue={handleContinue}
        />
      </StepContainer>
    );
  }

  if (step === 'PartnerDetails') {
    return (
      <StepContainer
        disabled={onboarding.partner.isActive}
        selectedStep={step}
        steps={stepTrackerSteps}
        onClickStep={(stepClicked) => setStep(stepClicked as StepKey)}>
        <PartnerDetails
          step={onboarding.getOnboardingStepByKey(step)!}
          areDocumentsSigned={onboarding.areDocumentsSigned}
          onContinue={handleContinue}
          onPrevious={handlePreviousStep}
        />
      </StepContainer>
    );
  }

  if (step === 'BankAccount') {
    return (
      <StepContainer
        disabled={onboarding.partner.isActive}
        selectedStep={step}
        steps={stepTrackerSteps}
        onClickStep={(stepClicked) => setStep(stepClicked as StepKey)}>
        <BankAccount
          step={onboarding.getOnboardingStepByKey('BankAccount')!}
          onContinue={handleContinue}
          onPrevious={handlePreviousStep}
        />
      </StepContainer>
    );
  }

  if (step === 'SignDocuments') {
    return (
      <StepContainer
        disabled={onboarding.partner.isActive}
        selectedStep={step}
        steps={stepTrackerSteps}
        onClickStep={(stepClicked) => setStep(stepClicked as StepKey)}>
        <SignDocuments
          isCompleted={onboarding.getOnboardingStepByKey('SignDocuments')!.isStepCompleted}
          onContinue={handleContinue}
          onPrevious={handlePreviousStep}
        />
      </StepContainer>
    );
  }

  if (step === 'Completed') {
    return (
      <StepContainer
        disabled={onboarding.partner.isActive}
        selectedStep={step}
        steps={stepTrackerSteps}
        onClickStep={(stepClicked) => setStep(stepClicked as StepKey)}>
        <Completed allowGoToPortal={onboarding.partner.isActive} />
      </StepContainer>
    );
  }

  return (
    <StepContainer
      disabled={onboarding.partner.isActive}
      selectedStep={step}
      steps={stepTrackerSteps}
      onClickStep={(stepClicked) => setStep(stepClicked as StepKey)}>
      <Greetings salesRepresentative={onboarding.salesRepresentative} disabled />
    </StepContainer>
  );
}
